import React from "react";
import { Link } from "react-router-dom";
import { X, Shield } from "lucide-react";

const STORAGE_KEY = "ci_consent";
const CONSENT_VERSION = "1";

const DEFAULT_CHOICES = {
  necessary: true, // always on
  analytics: false,
  marketing: false,
};

function readStored() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed.v !== CONSENT_VERSION) return null;
    return parsed;
  } catch {
    return null;
  }
}

function writeStored(choices) {
  try {
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ v: CONSENT_VERSION, choices, at: new Date().toISOString() }),
    );
  } catch {
    /* localStorage may be blocked */
  }
}

/** Push consent state to Google Consent Mode v2 */
function pushGtagConsent(choices) {
  if (typeof window === "undefined") return;
  window.dataLayer = window.dataLayer || [];
  function gtag() {
    window.dataLayer.push(arguments);
  }
  gtag("consent", "update", {
    ad_storage: choices.marketing ? "granted" : "denied",
    ad_user_data: choices.marketing ? "granted" : "denied",
    ad_personalization: choices.marketing ? "granted" : "denied",
    analytics_storage: choices.analytics ? "granted" : "denied",
  });
}

export default function CookieConsent() {
  const [choices, setChoices] = React.useState(DEFAULT_CHOICES);
  const [visible, setVisible] = React.useState(false);
  const [preferencesOpen, setPreferencesOpen] = React.useState(false);

  React.useEffect(() => {
    const stored = readStored();
    if (stored) {
      setChoices(stored.choices);
      pushGtagConsent(stored.choices);
    } else {
      setVisible(true);
    }

    const openHandler = () => {
      const s = readStored();
      if (s) setChoices(s.choices);
      setPreferencesOpen(true);
      setVisible(true);
    };
    window.addEventListener("open-cookie-preferences", openHandler);
    return () => window.removeEventListener("open-cookie-preferences", openHandler);
  }, []);

  const commit = (next) => {
    setChoices(next);
    writeStored(next);
    pushGtagConsent(next);
    setVisible(false);
    setPreferencesOpen(false);
  };

  const acceptAll = () => commit({ necessary: true, analytics: true, marketing: true });
  const rejectAll = () => commit({ necessary: true, analytics: false, marketing: false });
  const savePreferences = () => commit(choices);

  if (!visible) return null;

  return (
    <>
      {/* Backdrop when preferences modal open */}
      {preferencesOpen && (
        <div
          className="fixed inset-0 bg-zinc-950/40 z-[60] backdrop-blur-sm"
          onClick={() => setPreferencesOpen(false)}
          aria-hidden
        />
      )}

      <div
        role="dialog"
        aria-live="polite"
        aria-label="Cookie consent"
        data-testid="cookie-consent-banner"
        className={`fixed z-[70] transition-all duration-300 ${
          preferencesOpen
            ? "inset-0 flex items-center justify-center p-4"
            : "left-4 right-4 bottom-4 md:left-auto md:right-6 md:bottom-6 md:max-w-lg"
        }`}
      >
        <div
          className={`relative bg-white border border-zinc-900 shadow-2xl ${
            preferencesOpen ? "w-full max-w-2xl max-h-[90vh] overflow-y-auto" : ""
          }`}
        >
          <span className="corner-tick tl" />
          <span className="corner-tick tr" />
          <span className="corner-tick bl" />
          <span className="corner-tick br" />

          <div className="p-6 md:p-8">
            <div className="flex items-start justify-between gap-4">
              <div className="flex items-start gap-3">
                <Shield size={20} className="mt-0.5 text-[#C2410C]" strokeWidth={2} />
                <div>
                  <div className="tech-label">// Cookies</div>
                  <h3 className="font-display text-xl md:text-2xl font-bold uppercase mt-1 leading-tight">
                    We use a few cookies
                  </h3>
                </div>
              </div>
              {preferencesOpen && (
                <button
                  onClick={() => setPreferencesOpen(false)}
                  className="text-zinc-500 hover:text-zinc-900"
                  aria-label="Close preferences"
                  data-testid="cookie-close-preferences"
                >
                  <X size={20} />
                </button>
              )}
            </div>

            {!preferencesOpen ? (
              <>
                <p className="mt-4 text-sm text-zinc-700 leading-relaxed">
                  We use essential cookies to make the site work, and optional analytics and advertising cookies to measure how it&apos;s used. You can accept all, reject non-essential, or choose which to allow. See our{" "}
                  <Link to="/cookies" className="text-[#C2410C] underline">
                    Cookie Policy
                  </Link>.
                </p>

                <div className="mt-6 flex flex-col sm:flex-row flex-wrap gap-3">
                  <button
                    onClick={acceptAll}
                    className="btn-primary flex-1 justify-center"
                    data-testid="cookie-accept-all"
                  >
                    Accept all
                  </button>
                  <button
                    onClick={rejectAll}
                    className="btn-outline flex-1 justify-center"
                    data-testid="cookie-reject-all"
                  >
                    Reject non-essential
                  </button>
                  <button
                    onClick={() => setPreferencesOpen(true)}
                    className="btn-outline flex-1 justify-center"
                    data-testid="cookie-preferences"
                  >
                    Preferences
                  </button>
                </div>
              </>
            ) : (
              <>
                <p className="mt-4 text-sm text-zinc-700 leading-relaxed">
                  Choose which categories of cookies to allow. Your choice will be remembered for 12 months.
                </p>

                <div className="mt-6 space-y-3">
                  {[
                    {
                      key: "necessary",
                      title: "Necessary",
                      desc: "Required for the site to function. Cannot be disabled.",
                      locked: true,
                    },
                    {
                      key: "analytics",
                      title: "Analytics",
                      desc: "Google Analytics — helps us understand which pages are useful. Anonymised.",
                      locked: false,
                    },
                    {
                      key: "marketing",
                      title: "Advertising Measurement",
                      desc: "Google Ads conversion tracking — only used if you arrived from an ad.",
                      locked: false,
                    },
                  ].map((row) => (
                    <label
                      key={row.key}
                      className={`flex items-start gap-3 border border-zinc-200 p-4 ${
                        row.locked ? "bg-zinc-50" : "bg-white cursor-pointer hover:border-zinc-400"
                      }`}
                      data-testid={`cookie-toggle-${row.key}`}
                    >
                      <input
                        type="checkbox"
                        checked={choices[row.key]}
                        disabled={row.locked}
                        onChange={(e) =>
                          !row.locked &&
                          setChoices((c) => ({ ...c, [row.key]: e.target.checked }))
                        }
                        className="mt-1 w-4 h-4 accent-[#C2410C]"
                      />
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="font-display uppercase font-bold text-zinc-900">
                            {row.title}
                          </span>
                          {row.locked && (
                            <span className="text-[10px] font-mono uppercase tracking-[0.2em] text-zinc-500">
                              Always on
                            </span>
                          )}
                        </div>
                        <div className="text-sm text-zinc-600 mt-1">{row.desc}</div>
                      </div>
                    </label>
                  ))}
                </div>

                <div className="mt-6 flex flex-col sm:flex-row gap-3">
                  <button
                    onClick={savePreferences}
                    className="btn-primary flex-1 justify-center"
                    data-testid="cookie-save-preferences"
                  >
                    Save preferences
                  </button>
                  <button
                    onClick={acceptAll}
                    className="btn-outline flex-1 justify-center"
                    data-testid="cookie-accept-all-inside"
                  >
                    Accept all
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
